import type { Direction } from '../sim/direction';
import { directionForDelta } from '../sim/direction';
import type { CanStep, IsWalkable } from '../sim/mobile';

/**
 * Touch **tap-to-walk** routing: the pure decision core behind the mobile
 * "tap a tile and your character walks there" interaction (issue #160, PRD
 * #159). It is the third input-steering module alongside {@link keymap}
 * (keyboard) and {@link mouseMove} (right-click), and like them it holds **no
 * DOM and no Pixi** - `main.ts` owns the touch listeners and the per-tick
 * prediction-walker drive, this owns the routing maths so it unit-tests in node.
 *
 * Injected-dependency functions carry the whole pipe, so the thin glue can
 * stay untested:
 * - {@link resolveTarget} maps a tapped tile to a destination tile (or none).
 * - {@link findPath} routes from the player to that destination.
 * - {@link nextStep} yields the next {@link Direction} the **prediction
 *   walker** should walk along that route (the shipped client's path: the
 *   client plans *and* executes locally, per-step server-validated, so arrival
 *   is exact and orbit-free by construction).
 * - {@link assessWalk} is the progress watch for the alternative server-side
 *   route execution (`WalkPath`, ADR-0101), retained for pre-prediction
 *   clients and pinned by the `autowalk.netsim` harness.
 *
 * History: the tracer (#160, ADR-0065) shipped the open-field pipe; #162 made
 * {@link resolveTarget} **forgiving** (a sloppy tap resolves outward to the
 * nearest reachable tile; see `docs/adr/0090-tap-to-walk-forgiving-target.md`);
 * #161 swapped {@link findPath} for obstacle-avoiding **8-way A\*** with no
 * diagonal corner-cutting (see `docs/adr/0066-tap-to-walk-astar-routing.md`);
 * ADR-0101 moved route execution server-side to fix latency overshoot/orbiting,
 * and per-step prediction (ADR-0104) then moved the shipped client's execution
 * back to the local walker, which removes those artifacts at the source.
 */

/** A discrete tile coordinate. Structurally compatible with the renderer's `Tile`. */
export interface Tile {
  x: number;
  y: number;
}

/** Map extent injected into {@link resolveTarget} for the out-of-bounds clamp.
 * `undefined` means the map is unbounded (an open-plain world, PRD #387): no
 * clamp - every integer tile, negative included, is in-world. */
export interface Bounds {
  width: number;
  height: number;
}

/**
 * Per-walk progress bookkeeping threaded through {@link assessWalk} across
 * **snapshots** (the glue evaluates it once per server snapshot, the cadence at
 * which the avatar actually advances a tile - not per render frame). Owned by
 * the glue, reset on each retarget via {@link createWalkMonitor}.
 */
export interface WalkMonitor {
  /** Consecutive snapshots in which the avatar did not advance a tile. */
  stalls: number;
  /** Re-routes sent since the last forward progress; capped by {@link REPATH_LIMIT}. */
  repaths: number;
}

/** A fresh {@link WalkMonitor} for a new walk. */
export function createWalkMonitor(): WalkMonitor {
  return { stalls: 0, repaths: 0 };
}

/**
 * Per-route bookkeeping threaded through {@link nextStep} - the prediction
 * walker's steering seam. The glue evaluates it at step boundaries (a fresh
 * walker tile) and, while parked, at the 20Hz cadence the old snapshot-driven
 * decision ran at, so the stall/re-path tuning of #164 carries over. It carries
 * the re-path retry budget that keeps auto-walk alive when the world changes
 * under it: count blocked evaluations, cap the retries, stop on no route.
 * Owned by the glue, reset on each retarget.
 *
 * Re-path is **opt-in**: {@link nextStep} only routes around a block when the
 * glue has wired {@link RepathState.goal} and {@link RepathState.walkable}.
 * Bare state (both `null`) is the tracer's open-field walk - this is what keeps
 * the pure path-walking tests free of a live predicate.
 */
export interface RepathState {
  /**
   * Consecutive evaluations in which the avatar did not advance a tile.
   * Diagnostic counter (the cap rides on {@link RepathState.repaths}); reset on
   * progress.
   */
  stalls: number;
  /**
   * Re-paths spent since the last forward progress. A moving avatar refills the
   * budget (each advancing evaluation zeroes it); a genuinely stuck one burns it
   * down until it hits {@link nextStep}'s cap and the walk gives up.
   */
  repaths: number;
  /**
   * The walk's destination, fixed across re-paths so every reroute aims at the
   * same goal. `null` disables re-path (tracer fallback). Set by the glue at
   * retarget.
   */
  goal: Tile | null;
  /**
   * The live door-aware walkability predicate (static map AND no closed door),
   * refreshed by the glue each snapshot so a door that shuts mid-walk is seen.
   * `null` disables re-path. {@link nextStep} reads it both to spot a newly
   * blocked next tile and to recompute the route.
   */
  walkable: IsWalkable | null;
  /**
   * The edge-based climb rule for the walk's level (true terrain height, PRD
   * #536 Slice B): `GameMap.canStep` on the ground level, `null` on a roof
   * plate (flat surfaces get no climb rule) or when re-path is disabled.
   * Composed with {@link RepathState.walkable} by every route (re)plan and the
   * blocked-ahead probe, so a reroute never plans across a cliff edge. Flat
   * maps: the map's `canStep` is always-true, so behaviour is unchanged.
   */
  canStep: CanStep | null;
  /**
   * The active route. {@link nextStep} mirrors its `path` argument here and
   * **replaces** it on a reroute, so the glue reads the new route back out and
   * feeds it on the next evaluation.
   */
  path: Tile[];
  /**
   * Set when the walk stopped because it could not reach the goal (re-path cap
   * exceeded or no route at all), as opposed to a clean arrival/tap-own-tile
   * stop. The glue reads it to fire the "can't get there" signal (#163).
   */
  blocked: boolean;
}

/** A fresh {@link RepathState} for a new route (re-path inert until the glue wires `goal`+`walkable`). */
export function createRepathState(): RepathState {
  return { stalls: 0, repaths: 0, goal: null, walkable: null, canStep: null, path: [], blocked: false };
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

function sameTile(a: Tile, b: Tile): boolean {
  return a.x === b.x && a.y === b.y;
}

/**
 * How far (in tiles, Chebyshev/king-move distance) {@link resolveTarget} will
 * search outward from an **unwalkable** tap for a floor tile to forgive it to.
 *
 * A sloppy tap that lands a tile or three inside a wall or furniture still walks
 * the player to the nearest floor; but a tap *deeper* than this into solid
 * scenery - or out in the wide non-walkable letterbox terrain around the bank
 * (the map is the 32-wide bank block padded by {@link MAP_PADDING}=75 tiles of
 * rendered-but-unwalkable Britain on every side) - reads as a deliberate point
 * at somewhere unreachable and yields the "can't get there" result (`null`)
 * rather than sending the player on a long blind trek to distant floor. Bounding
 * the search is what keeps the resolved destination *near* where the thumb
 * landed (PRD #159 user stories 8 "as close as possible", 16 "no long blind
 * trek", 17 "edge tap -> nearest real tile").
 */
const FORGIVE_RADIUS = 4;

/**
 * The nearest in-world **walkable** tile to `(x, y)` within
 * {@link FORGIVE_RADIUS}, searched ring by ring outward (so the closest floor
 * wins), or `null` when no floor lies within the radius. `(x, y)` is assumed
 * already clamped into `bounds`. Within a ring, ties break to the smaller
 * squared-Euclidean distance (an orthogonal neighbour over a diagonal one) and
 * then to scan order, so the result is deterministic.
 */
function nearestWalkable(
  x: number,
  y: number,
  isWalkable: IsWalkable,
  bounds: Bounds | undefined,
): Tile | null {
  for (let r = 1; r <= FORGIVE_RADIUS; r++) {
    let best: Tile | null = null;
    let bestDist = Infinity;
    for (let ty = y - r; ty <= y + r; ty++) {
      if (bounds && (ty < 0 || ty >= bounds.height)) continue;
      for (let tx = x - r; tx <= x + r; tx++) {
        if (bounds && (tx < 0 || tx >= bounds.width)) continue;
        // Only the cells on this ring's perimeter (Chebyshev distance === r);
        // inner cells were covered by a smaller, nearer ring already.
        if (Math.max(Math.abs(tx - x), Math.abs(ty - y)) !== r) continue;
        if (!isWalkable(tx, ty)) continue;
        const dx = tx - x;
        const dy = ty - y;
        const d = dx * dx + dy * dy;
        if (d < bestDist) {
          bestDist = d;
          best = { x: tx, y: ty };
        }
      }
    }
    if (best) return best; // a nearer ring can never be beaten by a farther one
  }
  return null; // no floor within the forgiveness radius: can't get there
}

/**
 * Resolve a tapped tile to the destination the player should walk to, or `null`
 * when there is nowhere sensible to go ("can't get there" - the signal #163's
 * overlay renders).
 *
 * The tap is first rounded to a whole tile and **clamped into the map**, so an
 * out-of-bounds tap in the letterbox margin past the map edge snaps to the
 * nearest in-world tile. Then:
 *
 * - **Walkable** clamped tile -> that tile. (Tapping the player's own tile is
 *   just this case: it returns the tile, {@link findPath} yields an empty route,
 *   and the glue reads that as a stop - there is no dedicated stop control.)
 * - **Unwalkable** clamped tile (a wall, furniture, dead terrain) -> the nearest
 *   reachable floor tile found by an outward ring search ({@link nearestWalkable}),
 *   so a sloppy tap walks the player as close as possible to where they pointed.
 * - **No floor within {@link FORGIVE_RADIUS}** of the tap (a fully enclosed /
 *   unreachable target, or a tap deep in the non-walkable margin) -> `null`.
 *
 * "Reachable" here is *walkable within the forgiveness radius* - this seam has
 * only `isWalkable` + `bounds`, not the player's tile, so it cannot test graph
 * connectivity to the player. A resolved tile that turns out to be unroutable
 * (e.g. an enclosed pocket of floor) is caught downstream: {@link findPath}
 * returns an empty route and the glue stops. The downstream consumer
 * always receives this *resolved* destination, never the raw tapped tile, so the
 * #163 feedback matches the actual outcome.
 */
export function resolveTarget(
  tappedTile: Tile,
  isWalkable: IsWalkable,
  bounds: Bounds | undefined,
): Tile | null {
  // Unbounded (open-plain) maps have nothing to clamp against: every rounded
  // tile is in-world, negative coordinates included.
  const x = bounds ? clamp(Math.round(tappedTile.x), 0, bounds.width - 1) : Math.round(tappedTile.x);
  const y = bounds ? clamp(Math.round(tappedTile.y), 0, bounds.height - 1) : Math.round(tappedTile.y);
  if (isWalkable(x, y)) return { x, y };
  return nearestWalkable(x, y, isWalkable, bounds);
}

/**
 * Integer 8-way step costs (the classic `10`/`14` ≈ `1`/`√2` scaling): keeping
 * the cost and heuristic in integers makes the search deterministic - no float
 * accumulation - and an octile heuristic with these weights stays admissible, so
 * A* returns a genuine shortest route.
 */
const ORTHO_COST = 10;
const DIAG_COST = 14;

/**
 * How far outside the start↔goal bounding box A* may wander to route around an
 * obstacle. A real map's `isWalkable` is already false past the map edge, so the
 * search is naturally bounded there; this margin only matters for a degenerate
 * always-open predicate (e.g. a unit test's `() => true`), where it guarantees an
 * unreachable goal terminates the search instead of expanding outward forever.
 * Generous enough for any on-screen detour around the bank's walls.
 */
const SEARCH_MARGIN = 24;

/** The 8 grid neighbours: orthogonals first, then diagonals (a fixed order keeps the search deterministic). */
const NEIGHBOUR_OFFSETS: ReadonlyArray<readonly [number, number]> = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
  [1, 1],
  [1, -1],
  [-1, 1],
  [-1, -1],
];

function tileKey(x: number, y: number): string {
  return `${x},${y}`;
}

/** Octile distance: the exact 8-way cost over an open field, an admissible A* heuristic. */
function octile(ax: number, ay: number, bx: number, by: number): number {
  const dx = Math.abs(ax - bx);
  const dy = Math.abs(ay - by);
  const lo = Math.min(dx, dy);
  const hi = Math.max(dx, dy);
  return DIAG_COST * lo + ORTHO_COST * (hi - lo);
}

/**
 * Route from `start` to `goal`, returning the ordered list of tiles to step
 * through (**excluding** `start`, **including** `goal`). An empty list means
 * "no move": either already at the goal, the goal is itself unwalkable, or no
 * route the solver can find.
 *
 * **8-way A\*** over the injected `isWalkable` predicate (issue #161, PRD #159):
 * the route detours around walls, furniture, and **closed doors** (the glue
 * rebuilds a door-aware predicate from the live world on every re-path, so a door
 * that shuts mid-walk is respected), and returns empty when the goal is genuinely
 * unreachable rather than walking blindly into scenery.
 *
 * **No diagonal corner-cutting:** a diagonal step is admitted only when both
 * orthogonally-adjacent tiles are walkable, so the route never clips the corner
 * between two walls. This is a deliberately **client-only** rule - the server
 * validates only a step's destination tile (`Mobile.update` + `GameMap.isWalkable`),
 * so the stricter client never routes through a squeeze and never triggers a step
 * rejection or desync. See `docs/adr/0066-tap-to-walk-astar-routing.md`.
 *
 * **The climb rule** (true terrain height, PRD #536 Slice B) rides the optional
 * `canStep`: when supplied (the glue passes `GameMap.canStep` for a ground-level
 * walk), every expanded edge must also satisfy it, so the route detours to a
 * <= CLIMB ramp instead of planning across a cliff the server would reject.
 * Absent (roof-plate walks, legacy callers) or on a flat map (always-true) the
 * search is byte-for-byte the old cell-only A*.
 */
export function findPath(start: Tile, goal: Tile, isWalkable: IsWalkable, canStep?: CanStep): Tile[] {
  if (sameTile(start, goal)) return [];
  if (!isWalkable(goal.x, goal.y)) return []; // can't route onto a blocked goal

  // Bound the search to the start↔goal box plus a detour margin so a degenerate
  // always-open predicate can't expand forever toward an unreachable goal.
  const minX = Math.min(start.x, goal.x) - SEARCH_MARGIN;
  const maxX = Math.max(start.x, goal.x) + SEARCH_MARGIN;
  const minY = Math.min(start.y, goal.y) - SEARCH_MARGIN;
  const maxY = Math.max(start.y, goal.y) + SEARCH_MARGIN;

  const startKey = tileKey(start.x, start.y);
  const gScore = new Map<string, number>([[startKey, 0]]);
  const cameFrom = new Map<string, Tile>();
  const closed = new Set<string>();
  const open: Tile[] = [start];
  const openSet = new Set<string>([startKey]);

  while (open.length > 0) {
    // Pop the open node with the lowest f = g + h; ties go to the lowest h, which
    // keeps the route hugging the straight line toward the goal (and deterministic).
    let bestIdx = 0;
    let bestF = Infinity;
    let bestH = Infinity;
    for (let i = 0; i < open.length; i++) {
      const node = open[i]!;
      const g = gScore.get(tileKey(node.x, node.y))!;
      const h = octile(node.x, node.y, goal.x, goal.y);
      const f = g + h;
      if (f < bestF || (f === bestF && h < bestH)) {
        bestF = f;
        bestH = h;
        bestIdx = i;
      }
    }
    const current = open.splice(bestIdx, 1)[0]!;
    const curKey = tileKey(current.x, current.y);
    openSet.delete(curKey);

    if (sameTile(current, goal)) {
      // Reconstruct back to (but not including) start, then reverse to forward order.
      const path: Tile[] = [];
      let step: Tile | undefined = current;
      while (step && !sameTile(step, start)) {
        path.push(step);
        step = cameFrom.get(tileKey(step.x, step.y));
      }
      path.reverse();
      return path;
    }

    closed.add(curKey);

    for (const [dx, dy] of NEIGHBOUR_OFFSETS) {
      const nx = current.x + dx;
      const ny = current.y + dy;
      if (nx < minX || nx > maxX || ny < minY || ny > maxY) continue;
      const nKey = tileKey(nx, ny);
      if (closed.has(nKey)) continue;
      if (!isWalkable(nx, ny)) continue;
      // The edge-based climb rule (PRD #536 Slice B): a > CLIMB height edge is
      // impassable even between two individually-walkable cells.
      if (canStep && !canStep(current, { x: nx, y: ny })) continue;
      const diagonal = dx !== 0 && dy !== 0;
      // No diagonal corner-cutting: both orthogonal neighbours must be open.
      if (diagonal && (!isWalkable(current.x + dx, current.y) || !isWalkable(current.x, current.y + dy))) {
        continue;
      }
      const tentativeG = gScore.get(curKey)! + (diagonal ? DIAG_COST : ORTHO_COST);
      const knownG = gScore.get(nKey);
      if (knownG === undefined || tentativeG < knownG) {
        gScore.set(nKey, tentativeG);
        cameFrom.set(nKey, current);
        if (!openSet.has(nKey)) {
          open.push({ x: nx, y: ny });
          openSet.add(nKey);
        }
      }
    }
  }
  return []; // exhausted the reachable region without arriving: no route
}

/**
 * How many consecutive non-advancing snapshots {@link assessWalk} tolerates
 * before calling for a re-route. The server is walking the route itself, so a
 * healthy walk legitimately shows several unmoved snapshots in a row: a tile
 * advances only every ~2-3 snapshots at walk gait (250ms per tile against the
 * 10Hz snapshot cadence, ADR-0163), ~3-4 when a turn beat precedes the step, plus
 * the command's uplink latency at the start of a leg (~1-2 more at a 150ms one-way
 * mobile link). Twelve (~1.2s) sits comfortably past that worst healthy gap, so a
 * genuine block (a door shut mid-walk) is caught in just over a second without
 * ever re-routing a walk that is merely walking.
 */
export const STALL_SNAPSHOTS = 12;

/**
 * How many re-routes {@link assessWalk} will send without any forward progress
 * before it gives up and calls for the "can't get there" signal. Progress
 * refills the budget, so this only bites when the avatar is genuinely pinned
 * (parked in a freshly sealed pocket); with {@link STALL_SNAPSHOTS} between
 * attempts that is ~6s of patient retrying - enough to ride out foot traffic,
 * short enough that a truly blocked trip ends promptly.
 */
export const REPATH_LIMIT = 5;

/**
 * What the walk should do after this snapshot:
 * - `'walking'`: healthy (or within the stall tolerance) - do nothing.
 * - `'arrived'`: the avatar rests on the goal - clean stop, clear the marker.
 * - `'repath'`: progress has genuinely stalled - recompute the route from the
 *   current tile and send it as a fresh `WalkPath`.
 * - `'give-up'`: stalled through the whole re-route budget - stop and fire the
 *   "can't get there" signal (#163).
 */
export type WalkAssessment = 'walking' | 'arrived' | 'repath' | 'give-up';

/**
 * Watch one snapshot's worth of walk progress under **server-side route
 * execution** (`WalkPath`, ADR-0101). Driven once per **server snapshot** by
 * the glue while a walk is active; the server executes the route, so the
 * client's whole job is this: notice arrival, and notice the difference between
 * "still walking" (steps land only every few snapshots) and "genuinely stuck"
 * (the world changed under the route - re-plan), with a budget so a pinned
 * avatar stops with the blocked signal instead of re-planning forever. The
 * shipped predicting client steers through {@link nextStep} instead; this watch
 * remains for pre-prediction clients and the netsim harness that pins the
 * server-execution behaviour.
 */
export function assessWalk(
  current: Tile,
  goal: Tile,
  lastSnapshotMoved: boolean,
  monitor: WalkMonitor,
): WalkAssessment {
  if (sameTile(current, goal)) return 'arrived';
  if (lastSnapshotMoved) {
    // Forward progress: a moving avatar is not stuck, so both budgets refill.
    monitor.stalls = 0;
    monitor.repaths = 0;
    return 'walking';
  }
  monitor.stalls += 1;
  if (monitor.stalls < STALL_SNAPSHOTS) return 'walking';
  if (monitor.repaths >= REPATH_LIMIT) return 'give-up';
  monitor.stalls = 0;
  monitor.repaths += 1;
  return 'repath';
}

function dist2(a: Tile, b: Tile): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}

/**
 * The next tile along `path` the avatar should head for from `current`: the tile
 * after the one it currently stands on, or the head of the path when it has not
 * yet entered the route (still on `start`, which the path excludes).
 *
 * Divergence fallback: when `current` matches no path tile we must not fall back
 * to `path[0]` - that points the heading back toward the route's *start* and the
 * avatar reverses/oscillates. Instead we project `current` onto its nearest path
 * tile and head for the tile *after* it, always advancing toward the goal and
 * never back toward the start.
 */
function nextTileAfter(current: Tile, path: Tile[]): Tile {
  for (let i = 0; i < path.length; i++) {
    if (sameTile(path[i]!, current)) {
      return path[i + 1] ?? path[i]!; // already on the last tile: hold here
    }
  }
  // Off the planned path: project onto the nearest path tile and advance from
  // there, so the heading goes forward, never backward.
  let bestI = 0;
  let bestDist = Infinity;
  for (let i = 0; i < path.length; i++) {
    const d = dist2(path[i]!, current);
    if (d < bestDist) {
      bestDist = d;
      bestI = i;
    }
  }
  const target = path[bestI + 1] ?? path[bestI]!;
  return sameTile(target, current) ? current : target;
}

/**
 * How many re-path attempts {@link nextStep}'s auto-walk will spend trying to
 * get unstuck before it gives up and emits the "can't get there" signal. The
 * budget is measured *since the last forward progress*: a moving avatar refills
 * it on every advancing evaluation, so this only bites when the avatar is
 * genuinely pinned (a player parked on the sole route, a freshly sealed
 * pocket). The glue evaluates at the 20Hz cadence while parked, so eight stuck
 * evaluations is ~0.4s of waiting/retrying - long enough to ride out ordinary
 * foot traffic, short enough that a truly blocked trip ends promptly.
 */
const REPATH_CAP = 8;

/**
 * Decide the next {@link Direction} the **prediction walker** should walk, or
 * `null` to stop. Driven by the glue at step boundaries (the walker landed on a
 * fresh tile) and, while parked, at the 20Hz cadence - fed the player's current
 * tile, the active route, whether the latest evaluation advanced the avatar,
 * and the route's {@link RepathState}. The emitted direction flows into the
 * prediction walker as the held heading (`running:false`, `turnOnly:false`),
 * so a repeated direction is naturally deduped. Because the walker executes
 * locally and decides from its true tile, arrival is exact and orbit-free.
 *
 * Open-field walk (the tracer's behaviour, and the path with no re-path wired):
 * - An empty `path` (tap-own-tile, or no route) stops.
 * - Reaching the goal stops.
 * - Otherwise emit the heading toward the next tile via {@link directionForDelta}.
 *
 * **Re-path on block (#164).** When the glue has wired a `goal` and a live
 * `walkable` predicate into `repathState`, the walk stays alive as the world
 * changes under it. A re-path is triggered when either:
 * - the evaluation did **not** advance the avatar (`lastSnapshotMoved` false) -
 *   the walker is refusing a blocked step; or
 * - the next tile is **now blocked** in `walkable` (a door just closed), caught
 *   *before* heading into it rather than stalling against it.
 *
 * On a trigger it recomputes A* ({@link findPath}) to the same `goal` over the
 * fresh predicate and walks the new route. Guards keep it from thrashing:
 * - **No route** (`findPath` empty) -> stop immediately with the blocked signal.
 * - **Cap** - after {@link REPATH_CAP} re-paths without forward progress, stop with
 *   the blocked signal rather than re-routing forever. Each advancing evaluation
 *   resets the budget, so transient blockers (foot traffic, a momentarily occupied
 *   tile) are absorbed and only a genuinely pinned avatar reaches the cap.
 */
export function nextStep(
  current: Tile,
  path: Tile[],
  lastSnapshotMoved: boolean,
  repathState: RepathState,
): Direction | null {
  repathState.stalls = lastSnapshotMoved ? 0 : repathState.stalls + 1;
  // Forward progress refills the re-path budget: a moving avatar is not stuck.
  if (lastSnapshotMoved) repathState.repaths = 0;
  // Mirror the active route so the glue reads back any reroute below.
  repathState.path = path;

  if (path.length === 0) return null; // nothing to walk: stop
  const goal = path[path.length - 1]!;
  if (sameTile(current, goal)) return null; // arrived: stop

  // Re-path is opt-in (#164): only when the glue wired the goal + a live predicate.
  // Without them this stays the tracer's open-field walk.
  if (repathState.goal && repathState.walkable) {
    const ahead = nextTileAfter(current, path);
    const blockedAhead =
      !repathState.walkable(ahead.x, ahead.y) ||
      (repathState.canStep !== null && !repathState.canStep(current, ahead));
    if (!lastSnapshotMoved || blockedAhead) {
      if (repathState.repaths >= REPATH_CAP) {
        repathState.blocked = true;
        return null; // thrashed past the cap: give up with "can't get there"
      }
      const rerouted = findPath(current, repathState.goal, repathState.walkable, repathState.canStep ?? undefined);
      repathState.repaths += 1;
      if (rerouted.length === 0) {
        repathState.blocked = true;
        return null; // no route to the goal at all: stop immediately
      }
      repathState.path = rerouted;
      // Head for the first tile of the fresh route (it starts adjacent to `current`).
      const step = rerouted[0]!;
      return directionForDelta(step.x - current.x, step.y - current.y);
    }
  }

  const next = nextTileAfter(current, path);
  return directionForDelta(next.x - current.x, next.y - current.y);
}

/**
 * What the **route-driven** prediction walker should do after this evaluation
 * (ADR-0166). This is the tap-to-walk consumer the shipped client now uses in
 * place of {@link nextStep}: instead of emitting one held {@link Direction} per
 * frame (which made the {@link Mobile} overshoot every route bend and zig-zag),
 * the walker is handed the **whole route** via {@link Mobile.setPath} once, and
 * this only re-decides at the seams:
 *
 * - `'walking'` - stay on the current route; the mobile follows it tile-to-tile
 *   itself and parks exactly on the last tile (no per-frame steering needed).
 * - `'arrived'` - the goal is underfoot (or the route is empty): clean stop.
 * - `{ kind: 'reroute', route }` - the world changed under the walk (the walker
 *   is not advancing, or the next route tile is now blocked - a closed door), so
 *   A* re-planned to the same goal; the glue re-issues `walker.setPath(route)`.
 * - `'blocked'` - re-path thrashed past {@link REPATH_CAP} or found no route:
 *   stop and fire the "can't get there" signal (#163).
 *
 * The re-path *decision* is identical to {@link nextStep}'s (same triggers, same
 * {@link REPATH_CAP} budget, same {@link RepathState} bookkeeping) - only the
 * emitted result changes from a direction to a route. Re-path stays **opt-in**:
 * with no `goal`+`walkable` wired it degrades to plain arrival detection.
 */
export type RouteDecision =
  | 'walking'
  | 'arrived'
  | 'blocked'
  | { kind: 'reroute'; route: Tile[] };

export function nextRoute(
  current: Tile,
  path: Tile[],
  lastMoved: boolean,
  repathState: RepathState,
): RouteDecision {
  repathState.stalls = lastMoved ? 0 : repathState.stalls + 1;
  // Forward progress refills the re-path budget: a moving avatar is not stuck.
  if (lastMoved) repathState.repaths = 0;
  // Mirror the active route so the glue reads back any reroute below.
  repathState.path = path;

  if (path.length === 0) return 'arrived'; // nothing to walk: stop
  const goal = path[path.length - 1]!;
  if (sameTile(current, goal)) return 'arrived'; // parked on the goal: stop

  // Re-path is opt-in (#164): only when the glue wired the goal + a live predicate.
  if (repathState.goal && repathState.walkable) {
    const ahead = nextTileAfter(current, path);
    const blockedAhead =
      !repathState.walkable(ahead.x, ahead.y) ||
      (repathState.canStep !== null && !repathState.canStep(current, ahead));
    if (!lastMoved || blockedAhead) {
      if (repathState.repaths >= REPATH_CAP) {
        repathState.blocked = true;
        return 'blocked'; // thrashed past the cap: give up with "can't get there"
      }
      const rerouted = findPath(current, repathState.goal, repathState.walkable, repathState.canStep ?? undefined);
      repathState.repaths += 1;
      if (rerouted.length === 0) {
        repathState.blocked = true;
        return 'blocked'; // no route to the goal at all: stop immediately
      }
      repathState.path = rerouted;
      return { kind: 'reroute', route: rerouted };
    }
  }

  return 'walking'; // on the route, still moving: the mobile drives it itself
}
